import { useState, useEffect } from 'react';
import { getClient } from '@/lib/graphql/client';
import { GET_CURRENT_TENANT } from '@/lib/graphql/operations/settings';
import { useAuth } from '@/lib/auth/auth-context';
import { isTenantFeatureEnabled } from './tenant-feature-contract';

/** Feature flags are tenant-scoped. Never reuse one merchant's flags after a store switch. */
const featureCache = new Map<string, Record<string, boolean>>();

export function useTenantFeatures() {
  const { activeChannel } = useAuth();
  const channelId = activeChannel?.id;
  const [features, setFeatures] = useState<Record<string, boolean>>(
    channelId ? featureCache.get(channelId) ?? {} : {},
  );

  useEffect(() => {
    if (!channelId) {
      setFeatures({});
      return;
    }

    const cached = featureCache.get(channelId);
    if (cached) {
      setFeatures(cached);
      return;
    }

    // Fail closed during a store switch and while the new tenant's flags load.
    setFeatures({});
    let cancelled = false;

    const fetchFeatures = async () => {
      try {
        const client = getClient();
        const result = await client.query(GET_CURRENT_TENANT, {}).toPromise();
        const f = result.data?.currentTenant?.features;
        if (f && typeof f === 'object') {
          const nextFeatures = f as Record<string, boolean>;
          featureCache.set(channelId, nextFeatures);
          if (!cancelled) setFeatures(nextFeatures);
        }
      } catch {
        // Fail closed. Backend authorization remains the final gate.
      }
    };
    void fetchFeatures();

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const hasFeature = (key: string): boolean => {
    return isTenantFeatureEnabled(features, key);
  };

  return { features, hasFeature };
}
